import { useEffect, useRef, type MouseEvent, type UIEvent } from "react";

function updateProfileScrollAffordance(card: HTMLElement, scroller: HTMLElement = card) {
  const scrollBottom = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
  const hasScrollBelow = scrollBottom > 2;
  if (card.hasAttribute("data-profile-scroll-below") !== hasScrollBelow) {
    card.toggleAttribute("data-profile-scroll-below", hasScrollBelow);
  }
}

type ProfileCollapsePixelVar = readonly [name: string, fromVar: string, toVar: string, fromFallback: number, toFallback: number];

type ProfileCollapseMetrics = {
  collapseDistance: number;
  avatarExpandedSize: number;
  avatarCollapsedSize: number;
  avatarExpandedTop: number;
  avatarCollapsedTop: number;
  pixelVars: Array<readonly [name: string, from: number, to: number]>;
};

const PROFILE_COLLAPSE_PIXEL_VARS = [
  ["--profile-title-pad-left", "--profile-title-pad-left-expanded", "--profile-title-pad-left-collapsed", 140, 82],
  ["--profile-title-min-height", "--profile-title-min-height-expanded", "--profile-title-min-height-collapsed", 88, 56],
  ["--profile-title-padding-y", "--profile-title-padding-y-expanded", "--profile-title-padding-y-collapsed", 13, 6],
  ["--profile-title-name-size", "--profile-title-name-size-expanded", "--profile-title-name-size-collapsed", 25, 19],
  ["--profile-title-location-size", "--profile-title-location-size-expanded", "--profile-title-location-size-collapsed", 15, 12.5],
  ["--profile-kicker-height", "--profile-kicker-height-expanded", "--profile-kicker-height-collapsed", 14, 0],
  ["--profile-kicker-gap", "--profile-kicker-gap-expanded", "--profile-kicker-gap-collapsed", 5, 0],
] as const satisfies readonly ProfileCollapsePixelVar[];

const profileCollapseFrames = new WeakMap<HTMLElement, number>();
const profileCollapseMetrics = new WeakMap<HTMLElement, ProfileCollapseMetrics>();
const profileCollapseLastProgress = new WeakMap<HTMLElement, number>();

function getProfileCollapseMetrics(element: HTMLElement): ProfileCollapseMetrics {
  const cached = profileCollapseMetrics.get(element);
  if (cached) return cached;

  const styles = getComputedStyle(element);
  const cssNumber = (name: string, fallback: number) => {
    const parsed = Number.parseFloat(styles.getPropertyValue(name));
    return Number.isFinite(parsed) ? parsed : fallback;
  };
  const hero = element.querySelector<HTMLElement>(".profile-hero");
  const heroHeight = hero?.offsetHeight || 180;
  const metrics = {
    collapseDistance: Math.max(96, heroHeight - 52),
    avatarExpandedSize: cssNumber("--profile-avatar-size-expanded", 112),
    avatarCollapsedSize: cssNumber("--profile-avatar-size-collapsed", 52),
    avatarExpandedTop: cssNumber("--profile-avatar-top-expanded", -28),
    avatarCollapsedTop: cssNumber("--profile-avatar-top-collapsed", 2),
    pixelVars: PROFILE_COLLAPSE_PIXEL_VARS.map(([name, fromVar, toVar, fromFallback, toFallback]) => [
      name,
      cssNumber(fromVar, fromFallback),
      cssNumber(toVar, toFallback),
    ] as const),
  };

  profileCollapseMetrics.set(element, metrics);
  return metrics;
}

function resetProfileCollapseMetrics(element: HTMLElement) {
  profileCollapseMetrics.delete(element);
  profileCollapseLastProgress.delete(element);
}

function cancelProfileCollapseFrame(element: HTMLElement) {
  const frame = profileCollapseFrames.get(element);
  if (frame !== undefined) window.cancelAnimationFrame(frame);
  profileCollapseFrames.delete(element);
  profileCollapseLastProgress.delete(element);
  profileCollapseMetrics.delete(element);
}

function renderProfileCollapse(element: HTMLElement) {
  const metrics = getProfileCollapseMetrics(element);
  const progress = Math.min(1, Math.max(0, element.scrollTop / metrics.collapseDistance));
  const previousProgress = profileCollapseLastProgress.get(element);
  const shouldWriteProgress = previousProgress === undefined || Math.abs(previousProgress - progress) > 0.001;

  if (shouldWriteProgress) {
    const interpolate = (from: number, to: number) => from + ((to - from) * progress);
    const avatarScale = metrics.avatarExpandedSize > 0
      ? interpolate(1, metrics.avatarCollapsedSize / metrics.avatarExpandedSize)
      : 1;

    element.style.setProperty("--profile-avatar-scale", avatarScale.toFixed(4));
    element.style.setProperty("--profile-avatar-translate-y", `${interpolate(metrics.avatarExpandedTop, metrics.avatarCollapsedTop) - metrics.avatarExpandedTop}px`);

    for (const [name, from, to] of metrics.pixelVars) {
      element.style.setProperty(name, `${interpolate(from, to).toFixed(1)}px`);
    }

    element.style.setProperty("--profile-kicker-opacity", `${Math.max(0, 1 - (progress * 1.35)).toFixed(3)}`);
    profileCollapseLastProgress.set(element, progress);
  }

  const collapsed = progress > 0.72;
  if (element.hasAttribute("data-profile-collapsed") !== collapsed) {
    element.toggleAttribute("data-profile-collapsed", collapsed);
  }
  updateProfileScrollAffordance(element);
}

function scheduleProfileCollapse(element: HTMLElement) {
  if (profileCollapseFrames.has(element)) return;

  const frame = window.requestAnimationFrame(() => {
    profileCollapseFrames.delete(element);
    renderProfileCollapse(element);
  });
  profileCollapseFrames.set(element, frame);
}

export function updateProfileCollapse(event: UIEvent<HTMLElement>) {
  scheduleProfileCollapse(event.currentTarget);
}

export function updateProfileRegionScroll(event: UIEvent<HTMLDivElement>) {
  const card = event.currentTarget.closest<HTMLElement>(".match-profile-card");
  if (card) updateProfileScrollAffordance(card, event.currentTarget);
}

export function useProfileScrollAffordance() {
  const cardRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    const region = card.querySelector<HTMLElement>(".profile-scroll-region");
    const currentScroller = () => region && region.scrollHeight > region.clientHeight + 2 ? region : card;
    let frame: number | undefined;

    const sync = () => {
      resetProfileCollapseMetrics(card);
      scheduleProfileCollapse(card);
      if (frame) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => updateProfileScrollAffordance(card, currentScroller()));
    };

    sync();
    window.addEventListener("resize", sync);

    const observer = "ResizeObserver" in window ? new ResizeObserver(sync) : null;
    observer?.observe(card);
    if (region) observer?.observe(region);

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      cancelProfileCollapseFrame(card);
      window.removeEventListener("resize", sync);
      observer?.disconnect();
    };
  }, []);

  return cardRef;
}

export function scrollProfileCardToTop(event: MouseEvent<HTMLElement>) {
  const target = event.target as HTMLElement;
  if (target.closest("a, button, input, select, textarea, [role='button']")) return;

  const card = event.currentTarget.closest<HTMLElement>(".match-profile-card");
  card?.scrollTo({ top: 0, behavior: "smooth" });
  card?.querySelector<HTMLElement>(".profile-scroll-region")?.scrollTo({ top: 0, behavior: "smooth" });
}
